import React, { useState } from 'react';
import './Clients.css';
import useReveal from '../hooks/useReveal';
import CountUp from '../components/CountUp';
import TokenRevealText from '../components/TokenRevealText';

const clients = [
  {
    name: 'Analogics Tech India Ltd.',
    industry: 'Technology',
    desc: 'Redefining Technologies – leading technology solutions provider and parent group company of C2P.',
    initial: 'A',
    color: '#1f4b75',
    logo: '/clients/analogics.png',
  },
  {
    name: 'JARSH Safety',
    industry: 'Industrial Safety',
    desc: 'Redefines workplace protection with IoT-enabled smart safety solutions. Innovative products for predictive prevention and safer operations.',
    initial: 'JS',
    color: '#d4912a',
    logo: '/clients/jarsh-safety.png',
  },
  {
    name: 'Artlab India Pvt Ltd',
    industry: 'Laboratory Solutions',
    desc: 'Designs modular lab furniture, fume hoods, and turnkey lab solutions.',
    initial: 'AI',
    color: '#1f4b75',
    logo: '/clients/artlab-india.png',
  },
  {
    name: 'Panasonic',
    industry: 'Electronics',
    desc: 'Global leader in consumer and industrial electronics. Trusted partner for precision engineered components.',
    initial: 'P',
    color: '#1f4b75',
    logo: '/clients/panasonic.png',
  },
  {
    name: 'Bharat Dynamics Limited',
    industry: 'Defence',
    desc: 'Government of India undertaking in defence systems and guided missiles.',
    initial: 'BDL',
    color: '#5a6270',
    logo: '/clients/bdl.png',
  },
  {
    name: 'Electronics Corporation of India Ltd.',
    industry: 'Government / PSU',
    desc: 'A Department of Atomic Energy Enterprise – premier electronics company under Government of India.',
    initial: 'ECIL',
    color: '#5a6270',
    logo: '/clients/ecil.png',
  },
  {
    name: 'Epson',
    industry: 'IoT',
    desc: 'Exceed Your Vision – global leader in printing, imaging, and industrial automation solutions.',
    initial: 'E',
    color: '#1f4b75',
    logo: '/clients/epson.png',
  },
  {
    name: 'Apex Global Resources Ltd.',
    industry: 'Industrial',
    desc: 'Industrial resources, supply chain and engineering solutions partner.',
    initial: 'AG',
    color: '#d4912a',
    logo: '/clients/apex-global.png',
  },
  {
    name: 'Feedback Infra',
    industry: 'Infrastructure',
    desc: 'Making Infrastructure Happen – infrastructure development and engineering services across India.',
    initial: 'FI',
    color: '#d4912a',
    logo: '/clients/feedback-infra.png',
  },
  {
    name: 'IndusInd Bank',
    industry: 'Banking & Finance',
    desc: 'Leading private sector bank in India delivering innovative banking solutions.',
    initial: 'IB',
    color: '#5a6270',
    logo: '/clients/indusind-bank.png',
  },
];

const industries = [
  { name: 'Industrial Safety',  icon: '🦺' },
  { name: 'Laboratory Solutions', icon: '🧪' },
  { name: 'Electronics',      icon: '⚡' },
  { name: 'Defence',          icon: '🛡' },
  { name: 'IoT',              icon: '💻' },
  { name: 'Banking & Finance',icon: '🏦' },
  { name: 'Infrastructure',   icon: '🏗' },
  { name: 'Industrial',       icon: '⚙️' },
  { name: 'Technology',       icon: '🔬' },
  { name: 'Government / PSU', icon: '🏛' },
];

const CARDS_PER_SLIDE = 3;
const totalSlides = Math.ceil(clients.length / CARDS_PER_SLIDE);

const Clients = () => {
  useReveal();
  const [slide, setSlide] = useState(0);
  const [selectedStat, setSelectedStat] = useState(null);
  const [selectedIndustries, setSelectedIndustries] = useState(new Set());

  const toggleIndustry = (name) => {
    setSelectedIndustries(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const clearIndustries = () => setSelectedIndustries(new Set());

  const statsData = [
    { val: '40+', label: 'Full-time Workforce', icon: '👥', desc: 'Skilled professionals across Product Design, Operations, Tool Room, Production & QC' },
    { val: '20+', label: 'Active Clients',       icon: '🤝', desc: 'Trusted partnerships with leading organizations across Hyderabad and beyond' },
    { val: '5',   label: 'Core Departments',     icon: '🏢', desc: 'Product Design · Operations · Marketing · Tool Room · Production & Quality Control' },
    { val: '20+', label: 'Years in Business',    icon: '📅', desc: 'Over two decades of precision manufacturing experience since 2003' },
  ];

  const matchedClients = selectedIndustries.size > 0
    ? clients.filter(c => selectedIndustries.has(c.industry))
    : clients;

  const prev = () => setSlide(s => (s - 1 + totalSlides) % totalSlides);
  const next = () => setSlide(s => (s + 1) % totalSlides);

  return (
    <main className="clients-page page-enter">
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container reveal">
          <p className="section-eyebrow-light"><TokenRevealText text="Trusted By Many" speed="medium" /></p>
          <h1><TokenRevealText text="Our Clients" speed="medium" /></h1>
          <p>
            <TokenRevealText
              text="Long-term partnerships built on quality, reliability, and precision manufacturing."
              speed="medium"
            />
          </p>
        </div>
      </section>

      {/* ===== RESOURCES & HIGHLIGHTS ===== */}
      <section className="section section-alt clients-resources-section">
        <div className="container">
          <div className="resources-layout">
            <div className="resources-text reveal reveal-left">
              <p className="section-eyebrow"><TokenRevealText text="Resources & Reach" speed="medium" /></p>
              <h2 className="section-title"><TokenRevealText text="Our Strength" speed="medium" /></h2>
              <div className="divider" />
              <ul className="resources-bullets">
                <li>
                  <span className="rb-arrow">&#9658;</span>
                  <span>
                    <TokenRevealText
                      text="A 40+ full-time workforce, including Product Design, Operations, Marketing, Tool Room, Production & Quality Control"
                      speed="medium"
                    />
                  </span>
                </li>
                <li>
                  <span className="rb-arrow">&#9658;</span>
                  <span>
                    <TokenRevealText
                      text="Catering to requirements of 20+ clients in Hyderabad and aiming to expand nationally in the forthcoming years"
                      speed="medium"
                    />
                  </span>
                </li>
              </ul>
            </div>
            <div className="resources-stats reveal reveal-right">
              {statsData.map((s, i) => (
                <div
                  key={s.label}
                  className={`rs-card reveal reveal-scale reveal-d${i + 1}${selectedStat === s.label ? ' rs-card--active' : ''}`}
                  onClick={() => setSelectedStat(selectedStat === s.label ? null : s.label)}
                  role="button"
                  aria-pressed={selectedStat === s.label}
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && setSelectedStat(selectedStat === s.label ? null : s.label)}
                >
                  <span className="rs-icon">{s.icon}</span>
                  <span className="rs-val"><CountUp value={s.val} /></span>
                  <span className="rs-label"><TokenRevealText text={s.label} speed="medium" /></span>
                  {selectedStat === s.label && (
                    <p className="rs-desc"><TokenRevealText text={s.desc} speed="medium" /></p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CLIENTS GRID ===== */}
      <section className="section clients-partners-section">
        <div className="container">
          <div className="reveal" style={{ textAlign: 'center', maxWidth: '680px', margin: '0 auto 56px' }}>
            <p className="section-eyebrow"><TokenRevealText text="Our Partners" speed="medium" /></p>
            <h2 className="section-title centered"><TokenRevealText text="Trusted Client Network" speed="medium" /></h2>
            <div className="divider" style={{ margin: '12px auto 20px' }} />
            <p style={{ color: 'var(--text-gray)', fontSize: '1rem', lineHeight: '1.75' }}>
              <TokenRevealText
                text="Over the years, C2P Engineering Solutions has partnered with several leading organizations across different industries. We are proud to serve clients who demand the highest standards of precision and quality."
                speed="medium"
              />
            </p>
          </div>

          {/* Clients Carousel */}
          <div className="carousel-wrapper">
            <button className="carousel-arrow carousel-arrow-left" onClick={prev} aria-label="Previous">
              &#8249;
            </button>
            <div className="carousel-viewport">
              <div
                className="carousel-track"
                style={{ transform: `translateX(-${slide * 100}%)` }}
              >
                {Array.from({ length: totalSlides }, (_, pageIdx) => (
                  <div className="carousel-slide" key={pageIdx}>
                    {clients
                      .slice(pageIdx * CARDS_PER_SLIDE, (pageIdx + 1) * CARDS_PER_SLIDE)
                      .map((client, idx) => (
                        <div
                          key={client.name}
                          className={`client-profile-card reveal reveal-scale reveal-d${(idx % 3) + 1}`}
                        >
                        <div className="client-logo-frame" style={{ borderColor: client.color }}>
                          <img
                            src={`${process.env.PUBLIC_URL}${client.logo}`}
                            alt={`${client.name} logo`}
                            className="client-logo-img"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement?.classList.add('logo-fallback');
                            }}
                          />
                          <span className="client-logo-fallback" style={{ background: client.color }}>
                            {client.initial}
                          </span>
                        </div>
                        <div className="client-info">
                          <h3><TokenRevealText text={client.name} speed="medium" /></h3>
                          <span className="client-industry-badge"><TokenRevealText text={client.industry} speed="medium" /></span>
                          <p><TokenRevealText text={client.desc} speed="medium" /></p>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <button className="carousel-arrow carousel-arrow-right" onClick={next} aria-label="Next">
              &#8250;
            </button>
          </div>
          {/* Dots */}
          <div className="carousel-dots">
            {Array.from({ length: totalSlides }, (_, i) => (
              <button
                key={i}
                className={`carousel-dot ${i === slide ? 'active' : ''}`}
                onClick={() => setSlide(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== INDUSTRIES ===== */}
      <section className="section section-alt clients-industries-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <p className="section-eyebrow"><TokenRevealText text="Industries" speed="medium" /></p>
            <h2 className="section-title centered"><TokenRevealText text="Industries We Serve" speed="medium" /></h2>
            <div className="divider" style={{ margin: '12px auto 12px' }} />
            {selectedIndustries.size > 0 && (
              <div className="industry-meta">
                <span className="industry-count">
                  <TokenRevealText
                    text={`${selectedIndustries.size} selected – ${matchedClients.length} matching client${matchedClients.length !== 1 ? 's' : ''}`}
                    speed="medium"
                  />
                </span>
                <button className="industry-clear" onClick={clearIndustries}>
                  <TokenRevealText text="Clear all ✕" speed="medium" />
                </button>
              </div>
            )}
          </div>
          <div className="industries-chips">
            {industries.map((ind, i) => (
              <button
                key={ind.name}
                className={`industry-chip reveal reveal-d${i + 1}${selectedIndustries.has(ind.name) ? ' industry-chip--active' : ''}`}
                onClick={() => toggleIndustry(ind.name)}
                aria-pressed={selectedIndustries.has(ind.name)}
              >
                <span className="ind-icon">{ind.icon}</span>
                <span className="ind-label"><TokenRevealText text={ind.name} speed="medium" /></span>
                {selectedIndustries.has(ind.name) && <span className="ind-check">✓</span>}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="clients-cta">
        <div className="container clients-cta-inner">
          <h2><TokenRevealText text="Become our next success story" speed="medium" /></h2>
          <p><TokenRevealText text="Join industry leaders who trust C2P for precision manufacturing solutions." speed="medium" /></p>
          <a href="/contact" className="btn-primary"><TokenRevealText text="Let's Connect" speed="medium" /></a>
        </div>
      </section>
    </main>
  );
};

export default Clients;
